import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatUsd } from '#/shared/lib/format'
import { Button } from '#/shared/ui/button'
import { Input } from '#/shared/ui/input'

import { CARRIER_REQUEST_STATUS } from '../model/constants'
import type { CarrierRequestStatus } from '../model/constants'
import type {
  CarrierRequestPending,
  CarrierRequestPendingAction,
  CarrierRequestsHandlers,
} from '../model/types'

interface RequestActionsProps {
  requestId: string
  status: CarrierRequestStatus
  matchesCarrierPay: boolean
  brokerCounterPrice?: number
  distanceMiles?: number
  handlers: CarrierRequestsHandlers
  pending?: CarrierRequestPending | null
}

/**
 * While any request is mutating, every action button is disabled (no concurrent
 * transitions); only the exact button that was clicked shows its spinner.
 */
function actionState(
  pending: CarrierRequestPending | null | undefined,
  requestId: string,
  action: CarrierRequestPendingAction,
): { disabled: boolean; loading: boolean } {
  return {
    disabled: pending != null,
    loading: pending?.requestId === requestId && pending.action === action,
  }
}

export function RequestActions(props: RequestActionsProps) {
  switch (props.status) {
    case CARRIER_REQUEST_STATUS.pending:
      return <PendingActions {...props} />
    case CARRIER_REQUEST_STATUS.counter_sent:
      return <CancelCounterOfferAction {...props} />
    case CARRIER_REQUEST_STATUS.offer_sent:
      return <CancelOfferAction {...props} />
    case CARRIER_REQUEST_STATUS.declined:
    case CARRIER_REQUEST_STATUS.withdrawn:
    case CARRIER_REQUEST_STATUS.canceled:
      return <SendOfferLink {...props} />
    case CARRIER_REQUEST_STATUS.accepted:
      return null
    default:
      return null
  }
}

function PendingActions({
  requestId,
  matchesCarrierPay,
  brokerCounterPrice,
  distanceMiles,
  handlers,
  pending,
}: RequestActionsProps) {
  const { t } = useTranslation()
  const [counterRaw, setCounterRaw] = useState<string>(() =>
    brokerCounterPrice != null ? String(brokerCounterPrice) : '',
  )
  const counterValue = Number(counterRaw.replace(/,/g, ''))
  const counterValid = Number.isFinite(counterValue) && counterValue > 0
  const counterPerMile =
    counterValid && distanceMiles && distanceMiles > 0
      ? counterValue / distanceMiles
      : 0
  const decline = actionState(pending, requestId, 'decline')

  return (
    <div className="flex w-full flex-col gap-2">
      {!matchesCarrierPay && (
        <Input
          inputSize="sm"
          numberFormat
          value={counterRaw}
          disabled={pending != null}
          onChange={(e) => setCounterRaw(e.target.value)}
          placeholder={t('carrierRequests.counterOfferPlaceholder')}
          startAdornment={<span className="text-sm">$</span>}
          endAdornment={
            <span className="text-xs text-gray-500">
              {t('carrierRequests.pricePerMile', {
                value: formatUsd(counterPerMile, { showCents: true }),
              })}
            </span>
          }
        />
      )}
      <PrimaryAction
        requestId={requestId}
        matchesCarrierPay={matchesCarrierPay}
        counterValid={counterValid}
        counterValue={counterValue}
        handlers={handlers}
        pending={pending}
      />
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        disabled={decline.disabled}
        loading={decline.loading}
        className="w-full"
        onClick={() => handlers.onDecline?.(requestId)}
      >
        {t('carrierRequests.decline')}
      </Button>
    </div>
  )
}

function PrimaryAction({
  requestId,
  matchesCarrierPay,
  counterValid,
  counterValue,
  handlers,
  pending,
}: {
  requestId: string
  matchesCarrierPay: boolean
  counterValid: boolean
  counterValue: number
  handlers: CarrierRequestsHandlers
  pending?: CarrierRequestPending | null
}) {
  const { t } = useTranslation()
  if (matchesCarrierPay) {
    const state = actionState(pending, requestId, 'accept')
    return (
      <Button
        type="button"
        size="xs"
        pressEffect
        disabled={state.disabled}
        loading={state.loading}
        className="w-full"
        onClick={() => handlers.onAcceptRequest?.(requestId)}
      >
        {t('carrierRequests.acceptRequest')}
      </Button>
    )
  }
  if (counterValid) {
    const state = actionState(pending, requestId, 'counter')
    return (
      <Button
        type="button"
        size="xs"
        pressEffect
        disabled={state.disabled}
        loading={state.loading}
        className="w-full"
        onClick={() => handlers.onCounterOffer?.(requestId, counterValue)}
      >
        {t('carrierRequests.counterOffer')}
      </Button>
    )
  }
  const state = actionState(pending, requestId, 'accept')
  return (
    <Button
      type="button"
      size="xs"
      pressEffect
      disabled={state.disabled}
      loading={state.loading}
      className="w-full"
      onClick={() => handlers.onAcceptBid?.(requestId)}
    >
      {t('carrierRequests.acceptBid')}
    </Button>
  )
}

function CancelCounterOfferAction({
  requestId,
  handlers,
  pending,
}: RequestActionsProps) {
  const { t } = useTranslation()
  const state = actionState(pending, requestId, 'cancel')
  return (
    <div className="flex w-full flex-col">
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        disabled={state.disabled}
        loading={state.loading}
        className="w-full"
        onClick={() => handlers.onCancelCounterOffer?.(requestId)}
      >
        {t('carrierRequests.cancelCounterOffer')}
      </Button>
    </div>
  )
}

function CancelOfferAction({
  requestId,
  handlers,
  pending,
}: RequestActionsProps) {
  const { t } = useTranslation()
  const state = actionState(pending, requestId, 'cancel')
  return (
    <div className="flex w-full flex-col">
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        disabled={state.disabled}
        loading={state.loading}
        className="w-full"
        onClick={() => handlers.onCancelOffer?.(requestId)}
      >
        {t('carrierRequests.cancelOffer')}
      </Button>
    </div>
  )
}

function SendOfferLink({ requestId, handlers, pending }: RequestActionsProps) {
  const { t } = useTranslation()
  const state = actionState(pending, requestId, 'sendOffer')
  return (
    <div className="flex w-full items-start">
      <button
        type="button"
        disabled={state.disabled}
        aria-busy={state.loading}
        onClick={() => handlers.onSendOffer?.(requestId)}
        className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        {state.loading && (
          <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {t('carrierRequests.sendOffer')}
      </button>
    </div>
  )
}
